import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  RiTimerFlashLine,
  RiGasStationLine,
  RiGroupLine,
  RiFireLine,
  RiInboxLine,
  RiHistoryLine,
  RiCheckLine,
  RiArrowRightLine,
  RiArrowLeftLine,
} from "react-icons/ri";
import {
  LAST_ORDER_AGO_CHIPS,
  LAST_ORDER_AGO_DAYS_MAX,
  mapDaysToLastOrderAgoKey,
  buildDevDefaultSurveyRecord,
  type GasSurveyRecord,
  type LastOrderAgoKey,
  type SurveyNeedReason,
} from "@screen/Survey/surveyStorage";

/**
 * 開發預覽：寫死顯示完成過渡動畫（迴圈），不用填問卷。
 * 看完 UI 後改回 false。
 */
export const FORCE_SURVEY_FINISH_PREVIEW = false;

const C = {
  blue: "#2196f3",
  blueDark: "#1976d2",
  ink: "#1f2937",
  muted: "#8b909c",
  green: "#1d9e75",
  greenTint: "#e1f5ee",
  border: "#E8ECF2",
};

const STEPS = [
  {
    no: 1,
    icon: RiTimerFlashLine,
    accent: "#2196f3",
    title: "平常大概多久叫一次瓦斯？",
    hint: "選個大概就好，後續會根據您的實際情況自動調整",
    optional: false,
  },
  {
    no: 2,
    icon: RiGasStationLine,
    accent: "#0EA5E9",
    title: "家中使用的鋼瓶規格？",
    hint: "桶側鋼印有公斤數；不確定也沒關係",
    optional: false,
  },
  {
    no: 3,
    icon: RiHistoryLine,
    accent: "#14B8A6",
    title: "距上次叫桶大概多久了？",
    hint: "幫助估算「從哪一天開始算」；不記得可跳過",
    optional: true,
  },
  {
    no: 4,
    icon: RiGroupLine,
    accent: "#6366F1",
    title: "家中通常有幾個人？",
    hint: "人數影響用氣量估算",
    optional: false,
  },
  {
    no: 5,
    icon: RiFireLine,
    accent: "#F59E0B",
    title: "瓦斯主要用來做什麼？",
    hint: "可複選",
    optional: true,
  },
  {
    no: 6,
    icon: RiInboxLine,
    accent: "#64748B",
    title: "家裡有備瓶嗎？",
    hint: "點選後直接完成；有備瓶時，用完節奏可能不同",
    optional: true,
  },
] as const;

const TOTAL = STEPS.length;

const CYCLE_CHIPS = [
  { label: "1 週", days: 7 },
  { label: "2 週", days: 14 },
  { label: "約 1 個月", days: 30 },
  { label: "1~2個月", days: 45 },
  { label: "2~3個月", days: 75 },
  { label: "3~4個月", days: 105 },
  { label: "4~5個月", days: 135 },
  { label: "約半年", days: 180 },
  { label: "半年以上", days: 210 },
  { label: "一年以上", days: 365 },
];

const KG_CHIPS: { label: string; kg: number | null }[] = [
  { label: "4 kg", kg: 4 },
  { label: "16 kg", kg: 16 },
  { label: "20 kg", kg: 20 },
  { label: "50 kg", kg: 50 },
  { label: "不確定", kg: null },
];

const HOUSEHOLD_CHIPS = [
  { label: "1 人", value: "1" },
  { label: "2 人", value: "2" },
  { label: "3–4 人", value: "3-4" },
  { label: "5 人以上", value: "5+" },
];

const USAGE_CHIPS = [
  { label: "瓦斯爐", value: "cooking", hint: "煮飯炒菜" },
  { label: "熱水器", value: "hot_water", hint: "淋浴洗澡" },
  { label: "泡茶", value: "tea", hint: "煮水泡茶" },
];

const SPARE_CHIPS: { label: string; value: boolean | null }[] = [
  { label: "有備瓶", value: true },
  { label: "沒有", value: false },
  { label: "不確定", value: null },
];

type StepDir = "fwd" | "back";

const Chip = ({
  label,
  active,
  onPress,
  large,
}: {
  label: string;
  active: boolean;
  onPress: () => void;
  large?: boolean;
}) => (
  <button
    type="button"
    onClick={onPress}
    style={{
      padding: large ? "12px 16px" : "8px 13px",
      borderRadius: large ? 14 : 10,
      fontSize: large ? 14.5 : 13,
      fontWeight: active ? 700 : 500,
      fontFamily: "inherit",
      color: active ? "#fff" : C.ink,
      background: active ? C.blue : "#F7F8FB",
      border: `1.5px solid ${active ? C.blue : C.border}`,
      cursor: "pointer",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
      minHeight: large ? 48 : undefined,
      boxShadow: active && large ? "0 6px 16px -8px rgba(33,150,243,0.55)" : "none",
    }}>
    {label}
  </button>
);

/** 複選用途：每列左側 checkbox，避免跟單選 chip 搞混 */
const CheckOption = ({
  label,
  hint,
  active,
  onPress,
  large,
}: {
  label: string;
  hint?: string;
  active: boolean;
  onPress: () => void;
  large?: boolean;
}) => (
  <button
    type="button"
    onClick={onPress}
    aria-pressed={active}
    style={{
      display: "flex",
      alignItems: "center",
      gap: large ? 12 : 10,
      width: "100%",
      textAlign: "left",
      padding: large ? "12px 14px" : "10px 12px",
      borderRadius: large ? 14 : 12,
      fontFamily: "inherit",
      cursor: "pointer",
      background: active ? "rgba(33,150,243,0.08)" : "#F7F8FB",
      border: `1.5px solid ${active ? C.blue : C.border}`,
      transition: "background 0.15s ease, border-color 0.15s ease",
    }}>
    <span
      aria-hidden
      style={{
        width: large ? 22 : 20,
        height: large ? 22 : 20,
        borderRadius: 6,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: active ? C.blue : "#fff",
        border: `1.5px solid ${active ? C.blue : "#C5CCD8"}`,
        color: "#fff",
        transition: "background 0.15s ease, border-color 0.15s ease",
      }}>
      {active ? <RiCheckLine size={large ? 15 : 14} /> : null}
    </span>
    <span style={{ minWidth: 0, flex: 1 }}>
      <span
        style={{
          display: "block",
          fontSize: large ? 14.5 : 13.5,
          fontWeight: active ? 700 : 600,
          color: C.ink,
          lineHeight: 1.25,
        }}>
        {label}
      </span>
      {hint ? (
        <span
          style={{
            display: "block",
            marginTop: 2,
            fontSize: large ? 12 : 11.5,
            fontWeight: 500,
            color: C.muted,
            lineHeight: 1.3,
          }}>
          {hint}
        </span>
      ) : null}
    </span>
  </button>
);

export interface InlineSurveyProps {
  onComplete: (record: GasSurveyRecord) => void | Promise<void>;
  reason?: SurveyNeedReason | null;
  initialRecord?: GasSurveyRecord | null;
  /** 冷啟動主視覺：更大點擊區、更明顯進度 */
  spotlight?: boolean;
  /** DEV：顯示「一鍵填完並送出」 */
  enableDevQuickFill?: boolean;
}

export const InlineSurveyCard = ({
  onComplete,
  reason,
  initialRecord,
  spotlight = false,
  enableDevQuickFill = false,
}: InlineSurveyProps) => {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState<StepDir>("fwd");

  const initDays = initialRecord?.survey_days ?? null;
  const initFromChip = initDays != null && CYCLE_CHIPS.some((c) => c.days === initDays);

  const [surveyDays, setSurveyDays] = useState<number | null>(initFromChip ? initDays : null);
  const [customDays, setCustomDays] = useState(initFromChip || initDays == null ? "" : String(initDays));
  const [cylinderKg, setCylinderKg] = useState<number | null | undefined>(
    initialRecord ? initialRecord.cylinder_kg : undefined,
  );
  const initAgoDays = initialRecord?.last_order_ago_days ?? null;
  const initAgoFromChip =
    initialRecord?.last_order_ago != null &&
    LAST_ORDER_AGO_CHIPS.some(
      (c) => c.key === initialRecord.last_order_ago && c.days === initAgoDays,
    );
  const [lastOrderAgo, setLastOrderAgo] = useState<LastOrderAgoKey | null>(
    initAgoFromChip ? initialRecord?.last_order_ago ?? null : null,
  );
  const [customAgoDays, setCustomAgoDays] = useState(
    !initAgoFromChip && initAgoDays != null ? String(initAgoDays) : "",
  );
  const [householdSize, setHouseholdSize] = useState<string | null>(initialRecord?.household_size ?? null);
  const [usageTypes, setUsageTypes] = useState<string[]>(initialRecord?.usage_types ?? []);
  const [hasSpare, setHasSpare] = useState<boolean | null | undefined>(
    initialRecord ? initialRecord.has_spare_cylinder ?? undefined : undefined,
  );
  const [finishing, setFinishing] = useState(FORCE_SURVEY_FINISH_PREVIEW);
  const [finishReveal, setFinishReveal] = useState(false);
  const [finishLoopKey, setFinishLoopKey] = useState(0);
  const pendingRecordRef = useRef<GasSurveyRecord | null>(null);

  const effectiveDays = useMemo(() => {
    const n = Number(customDays);
    if (customDays && Number.isFinite(n) && n > 0) return Math.round(n);
    return surveyDays;
  }, [customDays, surveyDays]);

  const canProceed = [
    effectiveDays !== null,
    cylinderKg !== undefined,
    true,
    householdSize !== null,
    true,
    true,
  ][step];

  const cfg = STEPS[step];
  const isLast = step === TOTAL - 1;
  const Icon = cfg.icon;
  const progress = finishing ? 100 : ((step + 1) / TOTAL) * 100;

  const go = useCallback((next: number, direction: StepDir) => {
    setDir(direction);
    setStep(next);
  }, []);

  const buildRecord = useCallback(
    (spareOverride?: boolean | null): GasSurveyRecord | null => {
      if (effectiveDays === null) return null;
      const customAgo = Number(customAgoDays);
      const hasCustomAgo =
        customAgoDays !== "" && Number.isFinite(customAgo) && customAgo >= 0;
      const agoDays = hasCustomAgo
        ? Math.min(LAST_ORDER_AGO_DAYS_MAX, Math.round(customAgo))
        : (LAST_ORDER_AGO_CHIPS.find((c) => c.key === lastOrderAgo)?.days ?? null);
      const agoKey = hasCustomAgo
        ? mapDaysToLastOrderAgoKey(agoDays as number)
        : lastOrderAgo;
      const spare =
        spareOverride !== undefined
          ? spareOverride
          : hasSpare === undefined
            ? null
            : hasSpare;
      return {
        survey_days: effectiveDays,
        cylinder_kg: cylinderKg ?? null,
        household_size: householdSize,
        usage_types: usageTypes.length > 0 ? usageTypes : null,
        last_order_ago: agoKey,
        last_order_ago_days: agoDays,
        has_spare_cylinder: spare,
        observed_interval_days: null,
        completed_at: new Date().toISOString(),
      };
    },
    [
      effectiveDays,
      customAgoDays,
      lastOrderAgo,
      hasSpare,
      cylinderKg,
      householdSize,
      usageTypes,
    ],
  );

  const beginFinish = useCallback(
    async (spareOverride?: boolean | null) => {
      if (finishing) return;
      const record = buildRecord(spareOverride);
      if (!record) return;
      pendingRecordRef.current = record;
      setFinishing(true);
      setFinishReveal(false);
      try {
        // 等父層真正寫入／拉回結果後才結束；loading 不寫死秒數
        await Promise.resolve(onComplete(record));
        // 正式路徑：父層會卸掛問卷；此處維持 loading，不跳「已收到／估算完成」
        if (FORCE_SURVEY_FINISH_PREVIEW) setFinishReveal(true);
      } catch {
        setFinishing(false);
        setFinishReveal(false);
      }
    },
    [buildRecord, finishing, onComplete],
  );

  const handleDevQuickFill = useCallback(async () => {
    if (finishing) return;
    const record = buildDevDefaultSurveyRecord({ survey_days: 30 });
    pendingRecordRef.current = record;
    setFinishing(true);
    setFinishReveal(false);
    try {
      await Promise.resolve(onComplete(record));
      if (FORCE_SURVEY_FINISH_PREVIEW) setFinishReveal(true);
    } catch {
      setFinishing(false);
      setFinishReveal(false);
    }
  }, [finishing, onComplete]);

  useEffect(() => {
    if (!finishing || !FORCE_SURVEY_FINISH_PREVIEW) return;
    // 僅開發預覽迴圈；正式路徑由 beginFinish await onComplete
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setFinishReveal(true);
      return;
    }
    setFinishReveal(false);
    const reveal = window.setTimeout(() => setFinishReveal(true), 1000);
    const loop = window.setTimeout(() => {
      setFinishReveal(false);
      setFinishLoopKey((k) => k + 1);
    }, 2400);
    return () => {
      clearTimeout(reveal);
      clearTimeout(loop);
    };
  }, [finishing, finishLoopKey]);

  const handleNext = () => {
    if (isLast) void beginFinish();
    else go(step + 1, "fwd");
  };

  const handleSkip = () => {
    if (isLast) void beginFinish(null);
    else go(step + 1, "fwd");
  };

  const autoAdvance = (nextStep: number) => {
    window.setTimeout(() => go(nextStep, "fwd"), 220);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    fontSize: 14,
    fontFamily: "inherit",
    color: C.ink,
    background: "#FAFBFD",
    outline: "none",
  };

  const finMotionStyle = `
        @keyframes surveyDonePop {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes surveyFinRing {
          0% { transform: scale(0.6); opacity: 0.4; }
          80% { opacity: 0; }
          100% { transform: scale(2); opacity: 0; }
        }
        @keyframes surveyFinSpin {
          to { transform: rotate(360deg); }
        }
        @keyframes surveyFinDot {
          0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }
        @keyframes surveyFinBar {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(320%); }
        }
        @keyframes surveyFinRise {
          from { transform: translateY(6px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes surveyFinCheck {
          0% { transform: scale(0.5); opacity: 0; }
          65% { transform: scale(1.06); }
          100% { transform: scale(1); opacity: 1; }
        }
        .survey-done-pop  { animation: surveyDonePop 0.3s ease both; }
        .survey-fin-ring { position: absolute; inset: 0; border-radius: 9999px; border: 1.5px solid rgba(33,150,243,0.4); animation: surveyFinRing 1.7s ease-out infinite; }
        .survey-fin-spin {
          position: absolute;
          inset: 12px;
          border-radius: 9999px;
          border: 2.5px solid rgba(33,150,243,0.15);
          border-top-color: #2196f3;
          animation: surveyFinSpin 0.85s linear infinite;
        }
        .survey-fin-dot { animation: surveyFinDot 1.2s ease-in-out infinite; }
        .survey-fin-rise { animation: surveyFinRise 0.4s ease-out both; }
        .survey-fin-check { animation: surveyFinCheck 0.4s cubic-bezier(0.2,1.2,0.4,1) both; }
        @media (prefers-reduced-motion: reduce) {
          .survey-done-pop, .survey-fin-ring, .survey-fin-spin,
          .survey-fin-dot, .survey-fin-rise, .survey-fin-check { animation: none; }
        }
  `;

  /** 完成過渡：毛玻璃浮層（不是白 card），字色固定深色確保可讀 */
  if (finishing) {
    return (
      <>
        <style>{finMotionStyle}</style>
        <div
          className="survey-done-pop"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            padding: spotlight ? "8px 4px 20px" : "4px 0 16px",
            background: "transparent",
          }}>
          {FORCE_SURVEY_FINISH_PREVIEW ? (
            <div
              style={{
                alignSelf: "stretch",
                marginBottom: 10,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.04,
                color: spotlight ? "rgba(255,255,255,0.75)" : C.muted,
              }}>
              PREVIEW · FORCE_SURVEY_FINISH_PREVIEW=true
            </div>
          ) : null}
          <div
            style={{
              width: "100%",
              maxWidth: 340,
              borderRadius: 22,
              padding: "26px 22px 24px",
              background: "rgba(255,255,255,0.88)",
              border: "1px solid rgba(255,255,255,0.65)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              boxShadow: "0 10px 28px -14px rgba(15, 40, 80, 0.28)",
            }}>
            <div
              style={{
                position: "relative",
                width: 84,
                height: 84,
                margin: "0 auto 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
              {!finishReveal ? (
                <>
                  <span className="survey-fin-ring" style={{ animationDelay: "0s" }} />
                  <span className="survey-fin-ring" style={{ animationDelay: "0.55s" }} />
                  <span className="survey-fin-ring" style={{ animationDelay: "1.1s" }} />
                  <span className="survey-fin-spin" />
                  <span
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: 99,
                      background: C.blue,
                    }}
                  />
                </>
              ) : (
                <div
                  className="survey-fin-check"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#16A34A",
                  }}>
                  <RiCheckLine size={28} color="#fff" />
                </div>
              )}
            </div>

            {!finishReveal ? (
              <>
                <div className="survey-fin-rise" style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>
                  正在估算叫桶週期
                </div>
                <div
                  className="survey-fin-rise"
                  style={{
                    fontSize: 12.5,
                    color: C.muted,
                    marginTop: 6,
                    lineHeight: 1.45,
                    animationDelay: "0.08s",
                  }}>
                  根據您的用氣習慣快速分析
                </div>
                <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 14 }}>
                  {[0, 0.2, 0.4].map((d) => (
                    <span
                      key={d}
                      className="survey-fin-dot"
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: 99,
                        background: C.blue,
                        animationDelay: `${d}s`,
                      }}
                    />
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="survey-fin-rise" style={{ fontSize: 13.5, fontWeight: 700, color: "#16A34A" }}>
                  估算完成
                </div>
                <div
                  className="survey-fin-rise"
                  style={{
                    fontSize: 15.5,
                    fontWeight: 800,
                    color: C.ink,
                    marginTop: 4,
                    animationDelay: "0.06s",
                  }}>
                  正在帶入結果…
                </div>
                <div
                  className="survey-fin-rise"
                  style={{
                    position: "relative",
                    width: 132,
                    height: 3,
                    borderRadius: 99,
                    background: "#E8EEF5",
                    overflow: "hidden",
                    margin: "14px auto 0",
                    animationDelay: "0.12s",
                  }}>
                  <span
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      height: "100%",
                      width: "33%",
                      borderRadius: 99,
                      background: "#22C55E",
                      animation: "surveyFinBar 1.1s ease-in-out infinite",
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes surveySlideFwd {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes surveySlideBack {
          from { opacity: 0; transform: translateX(-20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes surveyDonePop {
          from { opacity: 0; transform: scale(0.97); }
          to   { opacity: 1; transform: scale(1); }
        }
        .survey-step-fwd  { animation: surveySlideFwd  0.28s cubic-bezier(0.22,1,0.36,1) both; }
        .survey-step-back { animation: surveySlideBack 0.28s cubic-bezier(0.22,1,0.36,1) both; }
        .survey-done-pop  { animation: surveyDonePop 0.32s ease both; }
        @media (prefers-reduced-motion: reduce) {
          .survey-step-fwd, .survey-step-back, .survey-done-pop { animation: none; }
        }
      `}</style>

      <div
        key={step}
        className={dir === "fwd" ? "survey-step-fwd" : "survey-step-back"}
        style={{
          background: "#fff",
          borderRadius: spotlight ? 20 : 16,
          padding: spotlight ? "18px 16px 14px" : "14px 14px 12px",
          boxShadow: spotlight
            ? "0 12px 32px -16px rgba(28,33,40,0.28), 0 2px 8px -4px rgba(28,33,40,0.08)"
            : "0 2px 10px -6px rgba(31,41,55,0.14)",
          border: spotlight ? "1px solid rgba(255,255,255,0.9)" : "none",
        }}>
        {enableDevQuickFill ? (
          <button
            type="button"
            disabled={finishing}
            onClick={() => void handleDevQuickFill()}
            style={{
              width: "100%",
              marginBottom: spotlight ? 12 : 10,
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px dashed rgba(124,58,237,0.55)",
              background: "rgba(124,58,237,0.08)",
              color: "#6D28D9",
              fontSize: 12,
              fontWeight: 700,
              fontFamily: "inherit",
              cursor: finishing ? "wait" : "pointer",
            }}>
            {finishing ? "DEV 送出中…" : "DEV · 一鍵填完並送出（30 天・真實 API）"}
          </button>
        ) : null}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: spotlight ? 16 : 12 }}>
          <div style={{ flex: 1, height: spotlight ? 5 : 3, borderRadius: 999, background: "#ECEEF2", overflow: "hidden" }}>
            <div
              style={{
                height: "100%",
                width: `${progress}%`,
                background: spotlight
                  ? "linear-gradient(90deg, #42A5F5, #1E88E5)"
                  : `linear-gradient(90deg, ${C.blue}, ${C.blueDark})`,
                borderRadius: 999,
                transition: "width 0.35s cubic-bezier(0.34,1.2,0.64,1)",
              }}
            />
          </div>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: C.muted, flexShrink: 0 }}>
            {step + 1}/{TOTAL}
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "flex-start", gap: spotlight ? 12 : 9, marginBottom: spotlight ? 14 : 10 }}>
          <span
            style={{
              width: spotlight ? 38 : 30,
              height: spotlight ? 38 : 30,
              borderRadius: spotlight ? 12 : 9,
              background: `${cfg.accent}14`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
            <Icon size={spotlight ? 18 : 15} color={cfg.accent} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: spotlight ? 17 : 15, fontWeight: 800, color: C.ink, lineHeight: 1.35 }}>{cfg.title}</span>
              {cfg.optional && (
                <span style={{ fontSize: 10, fontWeight: 600, color: C.muted, background: "#F1F2F5", padding: "1px 6px", borderRadius: 4 }}>
                  選填
                </span>
              )}
            </div>
            <div style={{ fontSize: spotlight ? 13 : 11.5, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>
              {reason === "refresh" && step === 0 ? "距上次填寫已一段時間，請更新您的用氣習慣" : cfg.hint}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: spotlight ? 14 : 12 }}>
          {step === 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: spotlight ? 8 : 6 }}>
                {CYCLE_CHIPS.map((c) => (
                  <Chip
                    key={c.days}
                    label={c.label}
                    large={spotlight}
                    active={!customDays && surveyDays === c.days}
                    onPress={() => {
                      setSurveyDays(c.days);
                      setCustomDays("");
                      autoAdvance(1);
                    }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  placeholder="自訂天數，如 45"
                  value={customDays}
                  onChange={(e) => setCustomDays(e.target.value)}
                  style={{ ...inputStyle, flex: 1, height: spotlight ? 48 : undefined, fontSize: spotlight ? 15 : 14 }}
                />
                <span style={{ fontSize: 12, color: C.muted }}>天</span>
              </div>
            </div>
          )}

          {step === 1 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: spotlight ? 8 : 6 }}>
              {KG_CHIPS.map((k) => (
                <Chip
                  key={k.label}
                  label={k.label}
                  large={spotlight}
                  active={cylinderKg === k.kg}
                  onPress={() => {
                    setCylinderKg(k.kg);
                    autoAdvance(2);
                  }}
                />
              ))}
            </div>
          )}

          {step === 2 && (
            <div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: spotlight ? 8 : 6 }}>
                {LAST_ORDER_AGO_CHIPS.map((c) => (
                  <Chip
                    key={c.key}
                    label={c.label}
                    large={spotlight}
                    active={!customAgoDays && lastOrderAgo === c.key}
                    onPress={() => {
                      setLastOrderAgo(c.key);
                      setCustomAgoDays("");
                      autoAdvance(3);
                    }}
                  />
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: spotlight ? 14 : 10 }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={LAST_ORDER_AGO_DAYS_MAX}
                  placeholder="自訂天數，如 40"
                  value={customAgoDays}
                  onChange={(e) => {
                    setCustomAgoDays(e.target.value);
                    setLastOrderAgo(null);
                  }}
                  style={{ ...inputStyle, flex: 1, height: spotlight ? 48 : undefined, fontSize: spotlight ? 15 : 14 }}
                />
                <span style={{ fontSize: 12, color: C.muted }}>天前</span>
              </div>
            </div>
          )}

          {step === 3 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: spotlight ? 8 : 6 }}>
              {HOUSEHOLD_CHIPS.map((h) => (
                <Chip
                  key={h.value}
                  label={h.label}
                  large={spotlight}
                  active={householdSize === h.value}
                  onPress={() => {
                    setHouseholdSize(h.value);
                    autoAdvance(4);
                  }}
                />
              ))}
            </div>
          )}

          {step === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: spotlight ? 8 : 6 }}>
              {USAGE_CHIPS.map((u) => (
                <CheckOption
                  key={u.value}
                  label={u.label}
                  hint={u.hint}
                  large={spotlight}
                  active={usageTypes.includes(u.value)}
                  onPress={() =>
                    setUsageTypes((prev) =>
                      prev.includes(u.value) ? prev.filter((v) => v !== u.value) : [...prev, u.value],
                    )
                  }
                />
              ))}
              {usageTypes.length > 0 ? (
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
                  已選 {usageTypes.length} 項
                </div>
              ) : null}
            </div>
          )}

          {step === 5 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: spotlight ? 8 : 6 }}>
              {SPARE_CHIPS.map((c) => (
                <Chip
                  key={c.label}
                  label={c.label}
                  large={spotlight}
                  active={hasSpare === c.value}
                  onPress={() => {
                    setHasSpare(c.value);
                    void beginFinish(c.value);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            paddingTop: 10,
            borderTop: `1px solid ${C.border}`,
          }}>
          {step > 0 ? (
            <button
              type="button"
              onClick={() => go(step - 1, "back")}
              style={{
                background: "none",
                border: "none",
                display: "flex",
                alignItems: "center",
                gap: 3,
                fontSize: 12.5,
                fontWeight: 600,
                color: C.muted,
                cursor: "pointer",
                fontFamily: "inherit",
                padding: "6px 0",
              }}>
              <RiArrowLeftLine size={14} />
              上一題
            </button>
          ) : (
            <span />
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {cfg.optional && (
              <button
                type="button"
                onClick={handleSkip}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: C.muted,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  padding: "6px 8px",
                }}>
                {isLast ? "跳過並完成" : "跳過"}
              </button>
            )}
            {!isLast && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: spotlight ? "12px 18px" : "8px 14px",
                  borderRadius: spotlight ? 12 : 10,
                  fontSize: spotlight ? 14.5 : 13,
                  fontWeight: 700,
                  fontFamily: "inherit",
                  color: "#fff",
                  background: canProceed
                    ? spotlight
                      ? "#2196f3"
                      : C.blue
                    : "#D1D5DB",
                  border: "none",
                  cursor: canProceed ? "pointer" : "default",
                  transition: "background 0.15s ease",
                  minHeight: spotlight ? 46 : undefined,
                }}>
                下一題
                <RiArrowRightLine size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
